import { useEffect, useMemo, useState } from 'react';
import AppLayout from '@/components/feature/AppLayout';

const API_BASE = 'https://guardiancloudcare.com/backend/api';

interface AuditRecord {
  id: string;
  uid: string | null;
  user_name: string | null;
  user_role: string | null;
  action: string;
  module: string | null;
  description: string | null;
  ip_address: string | null;
  created_at: string;
}

export default function AdminAuditTrailPage() {
  const [filter, setFilter] = useState('all');
  const [moduleFilter, setModuleFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [auditTrail, setAuditTrail] = useState<AuditRecord[]>([]);
  const [loading, setLoading] = useState(true);

  const loadAuditLogs = async () => {
    try {
      setLoading(true);

      const response = await fetch(
        `${API_BASE}/get_audit_logs.php`
      );

      if (!response.ok) {
        throw new Error(
          `HTTP error ${response.status}`
        );
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(
          result.error ||
            'Failed to load audit logs'
        );
      }

      const logs = Array.isArray(result.data)
        ? result.data
        : [];

      setAuditTrail(logs);
    } catch (error) {
      console.error(
        'Failed to load audit logs:',
        error
      );

      setAuditTrail([]);

      alert(
        'Failed to load audit trail. Please check the backend.'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAuditLogs();
  }, []);

  const modules = useMemo(() => {
    const uniqueModules = Array.from(
      new Set(
        auditTrail
          .map((a) => a.module)
          .filter(
            (module): module is string =>
              Boolean(module)
          )
      )
    );

    return ['all', ...uniqueModules];
  }, [auditTrail]);

  const isToday = (dateString: string) => {
    const date = new Date(dateString);
    const today = new Date();

    return (
      date.getFullYear() === today.getFullYear() &&
      date.getMonth() === today.getMonth() &&
      date.getDate() === today.getDate()
    );
  };

  const getStatus = (
    action: string,
    description: string = ''
  ) => {
    const actionText = action.toLowerCase().trim();
    const descriptionText =
      description.toLowerCase().trim();

    if (
      actionText.includes('failed') ||
      actionText.includes('denied') ||
      actionText.includes('error') ||
      descriptionText.includes('failed') ||
      descriptionText.includes('error') ||
      descriptionText.includes('denied')
    ) {
      return 'failed';
    }

    if (
      actionText.includes('warning') ||
      actionText.includes('pending') ||
      descriptionText.includes('warning') ||
      descriptionText.includes('pending')
    ) {
      return 'warning';
    }

    return 'success';
  };

  const filtered = auditTrail.filter((a) => {
    const status = getStatus(
      a.action,
      a.description || ''
    );

    const matchStatus =
      filter === 'all' ||
      status === filter;

    const matchModule =
      moduleFilter === 'all' ||
      a.module === moduleFilter;

    const searchText =
      search.toLowerCase().trim();

    const matchSearch =
      !searchText ||
      (a.user_name || '')
        .toLowerCase()
        .includes(searchText) ||
      (a.action || '')
        .toLowerCase()
        .includes(searchText) ||
      (a.module || '')
        .toLowerCase()
        .includes(searchText) ||
      (a.description || '')
        .toLowerCase()
        .includes(searchText) ||
      (a.user_role || '')
        .toLowerCase()
        .includes(searchText) ||
      (a.uid || '')
        .toLowerCase()
        .includes(searchText);

    return (
      matchStatus &&
      matchModule &&
      matchSearch
    );
  });

  const totalActionsToday =
    auditTrail.filter((a) =>
      isToday(a.created_at)
    ).length;

  const securityAlerts = auditTrail.filter(
    (a) =>
      getStatus(
        a.action,
        a.description || ''
      ) === 'failed'
  ).length;

  const warnings = auditTrail.filter(
    (a) =>
      getStatus(
        a.action,
        a.description || ''
      ) === 'warning'
  ).length;

  const escapeCsv = (value: unknown) => {
    const text = String(value ?? '');

    return `"${text.replace(/"/g, '""')}"`;
  };

  const exportLog = () => {
    if (filtered.length === 0) {
      alert(
        'There are no audit records to export.'
      );
      return;
    }

    const headers = [
      'ID',
      'UID',
      'User',
      'Role',
      'Action',
      'Module',
      'Description',
      'IP Address',
      'Created At',
    ];

    const rows = filtered.map((log) => [
      log.id,
      log.uid || '',
      log.user_name || '',
      log.user_role || '',
      log.action,
      log.module || '',
      log.description || '',
      log.ip_address || '',
      log.created_at,
    ]);

    const csv = [
      headers.map(escapeCsv).join(','),
      ...rows.map((row) =>
        row.map(escapeCsv).join(',')
      ),
    ].join('\n');

    const blob = new Blob(
      [csv],
      {
        type: 'text/csv;charset=utf-8;',
      }
    );

    const url =
      URL.createObjectURL(blob);

    const link =
      document.createElement('a');

    link.href = url;
    link.download = `audit-log-${new Date()
      .toISOString()
      .slice(0, 10)}.csv`;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);
  };

  return (
    <AppLayout
      title="Audit Trail"
      breadcrumb={[
        'Admin',
        'Audit Trail',
      ]}
    >
      <div className="grid grid-cols-3 gap-4 mb-6">
        {[
          {
            label: 'Total Actions Today',
            value: totalActionsToday,
            color: 'text-gray-800',
            bg: 'bg-gray-50',
            icon: 'ri-list-check-2',
          },
          {
            label: 'Security Alerts',
            value: securityAlerts,
            color: 'text-red-600',
            bg: 'bg-red-50',
            icon: 'ri-alarm-warning-line',
          },
          {
            label: 'Warnings',
            value: warnings,
            color: 'text-amber-600',
            bg: 'bg-amber-50',
            icon: 'ri-error-warning-line',
          },
        ].map((s) => (
          <div
            key={s.label}
            className={`${s.bg} rounded-xl border border-gray-100 p-5 flex items-center gap-4`}
          >
            <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center flex-shrink-0">
              <i
                className={`${s.icon} ${s.color} text-lg`}
              />
            </div>

            <div>
              <p
                className={`text-3xl font-bold ${s.color}`}
              >
                {s.value}
              </p>

              <p className="text-sm text-gray-500 mt-0.5">
                {s.label}
              </p>
            </div>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-xl px-4 py-2.5 flex-1 min-w-48">
          <i className="ri-search-line text-gray-400 text-sm" />

          <input
            value={search}
            onChange={(e) =>
              setSearch(e.target.value)
            }
            placeholder="Search by user, action, module..."
            className="bg-transparent text-sm outline-none w-full placeholder-gray-400 text-gray-700"
          />
        </div>

        <div className="flex gap-1 bg-white border border-gray-200 rounded-xl p-1">
          {[
            'all',
            'success',
            'warning',
            'failed',
          ].map((f) => (
            <button
              key={f}
              onClick={() =>
                setFilter(f)
              }
              className={`text-xs px-3 py-1.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap capitalize ${
                filter === f
                  ? 'bg-teal-500 text-white'
                  : 'text-gray-500 hover:bg-gray-100'
              }`}
            >
              {f}
            </button>
          ))}
        </div>

        <button
          onClick={exportLog}
          className="text-sm border border-gray-200 text-gray-600 px-4 py-2 rounded-xl hover:bg-gray-50 cursor-pointer whitespace-nowrap flex items-center gap-2 bg-white"
        >
          <i className="ri-download-line" />
          Export Log
        </button>
      </div>

      <div className="flex gap-2 flex-wrap mb-4">
        {modules.map((m) => (
          <button
            key={m}
            onClick={() =>
              setModuleFilter(m)
            }
            className={`text-xs px-3 py-1.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap capitalize ${
              moduleFilter === m
                ? 'bg-gray-800 text-white'
                : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'
            }`}
          >
            {m}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="px-5 py-12 text-center">
            <i className="ri-loader-4-line animate-spin text-3xl text-teal-500 mb-2 block" />

            <p className="text-sm text-gray-400">
              Loading audit records...
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-50">
            {filtered.map((a) => {
              const status =
                getStatus(
                  a.action,
                  a.description || ''
                );

              return (
                <div
                  key={a.id}
                  className={`px-5 py-4 flex items-start gap-4 ${
                    status === 'failed'
                      ? 'bg-red-50/40'
                      : status === 'warning'
                      ? 'bg-amber-50/40'
                      : ''
                  }`}
                >
                  <div
                    className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      status === 'success'
                        ? 'bg-green-100'
                        : status === 'failed'
                        ? 'bg-red-100'
                        : 'bg-amber-100'
                    }`}
                  >
                    <i
                      className={`text-sm ${
                        status === 'success'
                          ? 'ri-checkbox-circle-line text-green-600'
                          : status === 'failed'
                          ? 'ri-close-circle-line text-red-600'
                          : 'ri-error-warning-line text-amber-600'
                      }`}
                    />
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="text-sm font-semibold text-gray-800">
                          {a.action}

                          <span className="text-gray-400 font-normal">
                            {' '}—{' '}
                            {a.module ||
                              'System'}
                          </span>
                        </p>

                        <p className="text-xs text-gray-500 mt-0.5">
                          {a.description ||
                            'No description available.'}
                        </p>

                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2">
                          <p className="text-xs text-gray-400">
                            User:{' '}
                            <strong className="text-gray-600">
                              {a.user_name ||
                                'System'}
                            </strong>
                          </p>

                          <p className="text-xs text-gray-400">
                            Role:{' '}
                            <strong className="text-gray-600 capitalize">
                              {a.user_role ||
                                'system'}
                            </strong>
                          </p>

                          <p className="text-xs text-gray-400">
                            IP:{' '}
                            <strong className="text-gray-600">
                              {a.ip_address ||
                                '-'}
                            </strong>
                          </p>
                        </div>
                      </div>

                      <div className="flex flex-col items-end gap-1 flex-shrink-0">
                        <span className="text-xs text-gray-400 whitespace-nowrap">
                          {a.created_at}
                        </span>

                        <span
                          className={`text-xs px-2 py-0.5 rounded-full font-medium capitalize ${
                            status ===
                            'success'
                              ? 'bg-green-100 text-green-700'
                              : status ===
                                'failed'
                              ? 'bg-red-100 text-red-700'
                              : 'bg-amber-100 text-amber-700'
                          }`}
                        >
                          {status}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}

            {filtered.length === 0 && (
              <div className="px-5 py-12 text-center">
                <i className="ri-file-list-3-line text-3xl text-gray-300 mb-2 block" />

                <p className="text-sm text-gray-400">
                  No audit records match your filters
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </AppLayout>
  );
}